import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, WhatsappLogo, Printer, Gear, Check, ArrowsClockwise, X, Funnel, User, Calendar, ChartBar, PaperPlaneTilt, UserPlus, Compass } from '@phosphor-icons/react';
import API from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

// ─── Kleur-thema per prioriteit ────────────────────────────────────────────
const PRIO_STYLE = {
  paars: { bg: 'bg-purple-100', border: 'border-purple-400', text: 'text-purple-800', dot: 'bg-purple-500',  label: '🟣 Kritiek' },
  rood:  { bg: 'bg-red-50',     border: 'border-red-400',    text: 'text-red-800',    dot: 'bg-red-500',     label: '🔴 Geen aankoop' },
  oranje:{ bg: 'bg-amber-50',   border: 'border-amber-400',  text: 'text-amber-800',  dot: 'bg-amber-500',   label: '🟠 Deze maand nog niet' },
  groen: { bg: 'bg-emerald-50', border: 'border-emerald-400',text: 'text-emerald-800',dot: 'bg-emerald-500', label: '🟢 Op schema' },
  grijs: { bg: 'bg-gray-50',    border: 'border-gray-400',   text: 'text-gray-700',   dot: 'bg-gray-400',    label: '⚫ Geen data' },
};

const MAANDEN_NL = ['januari','februari','maart','april','mei','juni','juli','augustus','september','oktober','november','december'];
const fmtMaand = (iso) => {
  if (!iso) return '';
  const [y, m] = iso.split('-');
  const mIdx = parseInt(m, 10) - 1;
  return `${MAANDEN_NL[mIdx] || m} ${y}`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const [y,m,d] = iso.slice(0,10).split('-');
    return `${d}/${m}/${y}`;
  } catch { return iso; }
};

export default function KlantbezoekenPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ klanten: [], settings: { drempel_oranje: 30, drempel_rood: 60, drempel_paars: 90, drempel_aankoop_dagen: 60, landcode_whatsapp: '32' }, verkopers: [], totalen: { groen: 0, oranje: 0, rood: 0, paars: 0, nooit: 0, totaal: 0, geen_aankoop: 0, niet_deze_maand: 0, wel_deze_maand: 0 }, is_verkoper: false, vandaag: '' });
  const [busy, setBusy] = useState(false);
  const [filterVerkoper, setFilterVerkoper] = useState(() => localStorage.getItem('kb-filter-verkoper') || ''); // '' = alle, '__none__' = zonder, of verkoper_id
  const [filterStad, setFilterStad] = useState('');
  const [filterPrio, setFilterPrio] = useState(() => localStorage.getItem('kb-filter-prio') || ''); // '', 'paars', 'rood', 'oranje', 'groen', 'grijs'
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [whatsappOpen, setWhatsappOpen] = useState(false);
  const [historiekKlant, setHistoriekKlant] = useState(null);
  const [historiek, setHistoriek] = useState([]);

  const isVerkoper = (user?.role || '').toLowerCase() === 'verkoper';

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = {};
      if (filterVerkoper) params.verkoper_id = filterVerkoper;
      const r = await API.get('/klantbezoeken/lijst', { params });
      setData(prev => r.data || prev);
    } catch (e) {
      toast.error('Laden mislukt: ' + (e.response?.data?.detail || e.message));
    }
    setBusy(false);
  }, [filterVerkoper]);

  useEffect(() => {
    load();
    try { localStorage.setItem('kb-filter-verkoper', filterVerkoper); } catch (_e) { /* quota */ }
  }, [load, filterVerkoper]);

  useEffect(() => {
    try { localStorage.setItem('kb-filter-prio', filterPrio); } catch (_e) { /* quota */ }
  }, [filterPrio]);

  // ─── Filter + Zoek ─────────────────────────────────────────────
  const filtered = useMemo(() => {
    let arr = data.klanten || [];
    if (filterPrio) arr = arr.filter(k => k.prioriteit?.kleur === filterPrio);
    if (filterStad.trim()) {
      const q = filterStad.trim().toLowerCase();
      arr = arr.filter(k => (k.stad || '').toLowerCase().includes(q));
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      arr = arr.filter(k =>
        (k.naam || '').toLowerCase().includes(q) ||
        (k.klantnr || '').toLowerCase().includes(q) ||
        (k.email || '').toLowerCase().includes(q) ||
        (k.tel || '').toLowerCase().includes(q) ||
        (k.mobiel || '').toLowerCase().includes(q) ||
        (k.adres_short || '').toLowerCase().includes(q)
      );
    }
    return arr;
  }, [data.klanten, filterPrio, filterStad, search]);

  const allSelected = filtered.length > 0 && filtered.every(k => selectedIds.has(k.id));
  const toggleSelectAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map(k => k.id)));
  };
  const toggleSel = (id) => {
    setSelectedIds(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  // ─── Acties ────────────────────────────────────────────────
  const handleRegistreer = async (kid) => {
    try {
      const r = await API.post(`/klantbezoeken/${kid}/registreer`, {});
      toast.success('Bezoek geregistreerd');
      // Lokale update: zet laatste_bezoek op vandaag, herbereken status naar groen "op schema"
      const vandaag = r.data?.laatste_bezoek || new Date().toISOString().slice(0, 10);
      setData(prev => ({
        ...prev,
        klanten: prev.klanten.map(k => k.id === kid ? {
          ...k,
          laatste_bezoek: vandaag,
          bezoek_dagen: 0,
          prioriteit: { kleur: 'groen', label: 'op schema', dagen: 0, urgentie: 0, reden: 'ok' },
        } : k),
        totalen: {
          ...prev.totalen,
          wel_deze_maand: (prev.totalen.wel_deze_maand || 0) + 1,
          niet_deze_maand: Math.max(0, (prev.totalen.niet_deze_maand || 0) - 1),
        },
      }));
    } catch (e) {
      toast.error('Fout: ' + (e.response?.data?.detail || e.message));
      load();
    }
  };

  const handleBulkRegistreer = async () => {
    if (selectedIds.size === 0) { toast.warning('Selecteer eerst klanten'); return; }
    if (!window.confirm(`Markeer ${selectedIds.size} klanten als bezocht vandaag?`)) return;
    try {
      const r = await API.post('/klantbezoeken/bulk-registreer', { klant_ids: [...selectedIds] });
      toast.success(`${r.data.geregistreerd} bezoeken geregistreerd`);
      setSelectedIds(new Set());
      // Lokale update: alle geselecteerde klanten naar "op schema"
      const vandaag = r.data?.datum || new Date().toISOString().slice(0, 10);
      const verwerktSet = new Set(r.data?.geregistreerd > 0 ? [...selectedIds].filter(id => !(r.data.overgeslagen || []).includes(id)) : []);
      setData(prev => ({
        ...prev,
        klanten: prev.klanten.map(k => verwerktSet.has(k.id) ? {
          ...k,
          laatste_bezoek: vandaag,
          bezoek_dagen: 0,
          prioriteit: { kleur: 'groen', label: 'op schema', dagen: 0, urgentie: 0, reden: 'ok' },
        } : k),
      }));
      // Refresh van de tellers achteraf (kleine extra request, snel)
      load();
    } catch (e) {
      toast.error('Fout: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleUndo = async (kid) => {
    if (!window.confirm('Laatste bezoek ongedaan maken?')) return;
    try {
      const r = await API.post(`/klantbezoeken/${kid}/undo`);
      toast.success('Laatste bezoek verwijderd');
      // Lokale update: zet laatste_bezoek terug op vorige (uit response) — herberekening prio gebeurt bij volgende load
      setData(prev => ({
        ...prev,
        klanten: prev.klanten.map(k => k.id === kid ? { ...k, laatste_bezoek: r.data?.laatste_bezoek || '' } : k),
      }));
      // Refresh status na korte vertraging (om prio + tellers correct te krijgen)
      setTimeout(load, 250);
    } catch (e) {
      toast.error('Fout: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleHistoriek = async (klant) => {
    setHistoriekKlant(klant);
    try {
      const r = await API.get(`/klantbezoeken/${klant.id}/historiek`);
      setHistoriek(r.data || []);
    } catch {
      setHistoriek([]);
    }
  };

  // Inline verkoper-toewijzing (admin-only) — UPDATE LOKAAL zonder full reload (anders scrollt tabel naar boven)
  const handleVerkoperChange = async (kid, newVid) => {
    // Optimistische update: pas direct lokaal aan
    const verkoper = data.verkopers.find(v => v.id === newVid);
    setData(prev => ({
      ...prev,
      klanten: prev.klanten.map(k => k.id === kid ? { ...k, verkoper_id: newVid || '', verkoper_naam: verkoper ? verkoper.naam : '' } : k),
    }));
    try {
      await API.put(`/klantbezoeken/${kid}/verkoper`, { verkoper_id: newVid || '' });
      toast.success(newVid ? 'Verkoper toegewezen' : 'Toewijzing gewist');
    } catch (e) {
      toast.error('Fout: ' + (e.response?.data?.detail || e.message));
      // Bij fout: rollback via full reload
      load();
    }
  };

  // WhatsApp naar klant via bestaande Communicatie-hub (deep-link)
  const handleKlantWhatsapp = (klant) => {
    const tel = (klant.mobiel || klant.tel || '').trim();
    if (!tel) {
      toast.warning('Klant heeft geen telefoonnummer');
      return;
    }
    const prefillText = `Beste ${klant.naam},\n\nIk plan een bezoek bij u. Wanneer schikt het best?\n\nMet vriendelijke groet`;
    navigate(`/whatsapp?klant_id=${klant.id}&prefilltext=${encodeURIComponent(prefillText)}`);
  };

  // Bulk WhatsApp via Hub — open Hub voor elke geselecteerde klant
  const handleBulkWhatsappHub = () => {
    if (selectedIds.size === 0) { toast.warning('Selecteer eerst klanten'); return; }
    // Pak eerste klant met telefoon
    const klantenMetTel = filtered
      .filter(k => selectedIds.has(k.id) && (k.mobiel || k.tel));
    if (klantenMetTel.length === 0) {
      toast.warning('Geen van de geselecteerde klanten heeft een telefoonnummer');
      return;
    }
    // Open de WhatsApp Hub met de eerste klant + queue-flag
    const first = klantenMetTel[0];
    const queue = klantenMetTel.slice(1).map(k => k.id).join(',');
    const prefillText = `Beste ${first.naam},\n\nIk plan vandaag een bezoek bij u. Wanneer schikt het best?\n\nMet vriendelijke groet`;
    const url = `/whatsapp?klant_id=${first.id}&prefilltext=${encodeURIComponent(prefillText)}${queue ? '&queue=' + encodeURIComponent(queue) : ''}`;
    navigate(url);
  };

  const handlePrint = () => {
    // Als er een selectie is, voeg een tijdelijke class toe aan body zodat CSS enkel selected rijen toont
    if (selectedIds.size > 0) {
      document.body.classList.add('kb-print-selected-only');
    } else {
      document.body.classList.remove('kb-print-selected-only');
    }
    setTimeout(() => {
      window.print();
      setTimeout(() => document.body.classList.remove('kb-print-selected-only'), 500);
    }, 50);
  };

  // Excel export — opent download via backend
  const handleExcelExport = () => {
    const token = localStorage.getItem('emergent_billing_token') || '';
    const apiBase = process.env.REACT_APP_BACKEND_URL || '';
    const params = new URLSearchParams();
    if (filterVerkoper) params.append('verkoper_id', filterVerkoper);
    // Als gebruiker klanten heeft geselecteerd → enkel die exporteren
    if (selectedIds.size > 0) {
      params.append('klant_ids', [...selectedIds].join(','));
    }
    const url = `${apiBase}/api/klantbezoeken/export-excel${params.toString() ? '?' + params.toString() : ''}`;
    fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => {
        if (!r.ok) throw new Error('Excel-export mislukt');
        return r.blob();
      })
      .then(blob => {
        const u = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = u;
        const verkoperLabel = filterVerkoper && filterVerkoper !== '__none__'
          ? (data.verkopers.find(v => v.id === filterVerkoper)?.naam || 'verkoper').replace(/[^a-zA-Z0-9]/g, '_')
          : 'alle';
        const suffix = selectedIds.size > 0 ? `_${selectedIds.size}_geselecteerd` : '';
        a.download = `Bezoekplanning_${verkoperLabel}${suffix}_${data.vandaag || new Date().toISOString().slice(0,10)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(u);
        toast.success(selectedIds.size > 0 ? `${selectedIds.size} geselecteerde klanten geëxporteerd` : 'Excel gedownload');
      })
      .catch(e => toast.error('Fout: ' + e.message));
  };

  // ─── WhatsApp link genereren ───────────────────────────────
  const [whatsappData, setWhatsappData] = useState(null);
  const [whatsappVerkoperId, setWhatsappVerkoperId] = useState('');
  const [whatsappGroups, setWhatsappGroups] = useState([]);  // {verkoper_id, verkoper_naam, klant_ids[], wa_url, tekst, telefoon}

  // Tel klanten per verkoper voor de geselecteerde set
  const selectedByVerkoper = useMemo(() => {
    const out = {};
    filtered.filter(k => selectedIds.has(k.id)).forEach(k => {
      const vid = k.verkoper_id || '__none__';
      if (!out[vid]) {
        out[vid] = {
          verkoper_id: k.verkoper_id || '',
          verkoper_naam: k.verkoper_naam || '— niet toegewezen —',
          klant_ids: [],
        };
      }
      out[vid].klant_ids.push(k.id);
    });
    return Object.values(out);
  }, [filtered, selectedIds]);

  const openWhatsapp = () => {
    if (selectedIds.size === 0) {
      toast.warning('Selecteer eerst klanten om te versturen');
      return;
    }
    // Default verkoper: meest voorkomende onder geselecteerden (anders eerste toegewezen, anders huidige filter)
    let defaultVid = '';
    if (selectedByVerkoper.length > 0) {
      // Sorteer op aantal klanten DESC, neem de eerste met een verkoper_id
      const sorted = [...selectedByVerkoper].sort((a, b) => b.klant_ids.length - a.klant_ids.length);
      const firstWithVerkoper = sorted.find(g => g.verkoper_id);
      if (firstWithVerkoper) defaultVid = firstWithVerkoper.verkoper_id;
    }
    if (!defaultVid && isVerkoper && user?.verkoper_id) defaultVid = user.verkoper_id;
    if (!defaultVid && filterVerkoper && filterVerkoper !== '__none__') defaultVid = filterVerkoper;
    setWhatsappVerkoperId(defaultVid);
    setWhatsappData(null);
    setWhatsappGroups([]);
    setWhatsappOpen(true);
  };

  // Verstuur GROEP per verkoper — genereer 1 wa.me link per verkoper
  const handleVerstuurPerVerkoper = async () => {
    const groups = selectedByVerkoper.filter(g => g.verkoper_id);  // skip 'niet toegewezen'
    if (groups.length === 0) {
      toast.warning('Geen geselecteerde klant heeft een toegewezen verkoper');
      return;
    }
    const result = [];
    for (const g of groups) {
      try {
        const r = await API.post('/klantbezoeken/whatsapp-link', {
          verkoper_id: g.verkoper_id,
          klant_ids: g.klant_ids,
        });
        result.push({ ...g, wa_url: r.data.wa_url, tekst: r.data.tekst, telefoon: r.data.telefoon });
      } catch (e) {
        toast.error(`Fout voor ${g.verkoper_naam}: ${e.response?.data?.detail || e.message}`);
      }
    }
    setWhatsappGroups(result);
  };

  const genereerWhatsappLink = async () => {
    try {
      const r = await API.post('/klantbezoeken/whatsapp-link', {
        verkoper_id: whatsappVerkoperId || undefined,
        klant_ids: [...selectedIds],
      });
      setWhatsappData(r.data);
    } catch (e) {
      toast.error('Fout: ' + (e.response?.data?.detail || e.message));
    }
  };

  useEffect(() => {
    if (whatsappOpen) genereerWhatsappLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whatsappOpen, whatsappVerkoperId]);

  // ─── Render ────────────────────────────────────────────────────
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  return (
    <div className="min-h-screen -m-4 sm:-m-6 p-4 sm:p-6 bg-gradient-to-b from-slate-50 to-slate-100" data-testid="klantbezoeken-page">
      <div className="max-w-7xl mx-auto space-y-4">
      {/* Print-only header */}
      <div className="hidden print:block mb-6">
        <h1 className="text-2xl font-bold">Maandplanning — {fmtMaand(data.vandaag)}</h1>
        {filterVerkoper && filterVerkoper !== '__none__' && (
          <div className="text-sm text-gray-700">Verkoper: {data.verkopers.find(v => v.id === filterVerkoper)?.naam || '—'}</div>
        )}
      </div>

      {/* Header — Maandplanning titel + Verkoper-dropdown + Afdrukken */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 print:hidden pt-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Maandplanning</h1>
          <div className="text-xs sm:text-sm text-slate-500 mt-1">
            Klanten met openstaande opvolging staan bovenaan. Vink een bezoek af zodra het gebeurd is.
          </div>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <button
            type="button"
            onClick={() => navigate('/klantbezoeken/route')}
            className="px-3 sm:px-4 py-2 text-sm rounded-lg bg-[#002FA7] hover:bg-[#001A66] text-white font-semibold inline-flex items-center gap-1.5 shadow-sm"
            data-testid="kb-route-btn"
            title="Bezoekroute plannen op postcode (PDF, Excel, WhatsApp, gsm-checklist)"
          >
            <Compass size={16} weight="bold" />
            <span className="hidden sm:inline">Bezoekroute</span> plannen
          </button>
          {!isVerkoper && (
            <div className="flex items-center bg-white border border-slate-200 rounded-lg px-2 sm:px-3 py-2 gap-2 shadow-sm flex-1 sm:flex-initial min-w-0">
              <label className="text-xs sm:text-sm text-slate-600 hidden sm:inline">Verkoper</label>
              <select
                value={filterVerkoper}
                onChange={e => setFilterVerkoper(e.target.value)}
                className="text-xs sm:text-sm font-medium bg-white outline-none cursor-pointer flex-1 min-w-0"
                data-testid="kb-filter-verkoper"
              >
                <option value="">Alle verkopers</option>
                <option value="__none__">⚠️ Zonder verkoper</option>
                {(data.verkopers || []).map(v => (
                  <option key={v.id} value={v.id}>{v.naam}</option>
                ))}
              </select>
            </div>
          )}
          <div className="relative">
            <button
              type="button"
              onClick={() => setExportMenuOpen(o => !o)}
              className="px-4 py-2 text-sm rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5 shadow-sm font-medium"
              data-testid="kb-print-btn"
            >
              <Printer size={16} />
              Afdrukken
              <span className="text-xs text-slate-400">▾</span>
            </button>
            {exportMenuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setExportMenuOpen(false)} />
                <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-20 min-w-[260px]" data-testid="kb-export-menu">
                  {selectedIds.size > 0 && (
                    <div className="px-3 py-2 text-xs text-blue-700 bg-blue-50 border-b border-blue-100">
                      🔵 {selectedIds.size} geselecteerde klant{selectedIds.size === 1 ? '' : 'en'} wordt geëxporteerd
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => { setExportMenuOpen(false); handleExcelExport(); }}
                    className="w-full px-3 py-2 text-sm text-left hover:bg-slate-50 inline-flex items-center gap-2"
                    data-testid="kb-export-excel"
                  >
                    📊 <div className="flex-1">
                      <div className="font-medium">Excel-bestand</div>
                      <div className="text-xs text-slate-500">
                        {selectedIds.size > 0 ? `${selectedIds.size} geselecteerd` : `${filtered.length} klanten`} · opvolging-template
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => { setExportMenuOpen(false); handlePrint(); }}
                    className="w-full px-3 py-2 text-sm text-left hover:bg-slate-50 inline-flex items-center gap-2"
                    data-testid="kb-export-print"
                  >
                    🖨️ <div className="flex-1">
                      <div className="font-medium">Print (browser)</div>
                      <div className="text-xs text-slate-500">
                        {selectedIds.size > 0 ? `${selectedIds.size} geselecteerd` : `${filtered.length} klanten`} · PDF / papier
                      </div>
                    </div>
                  </button>
                </div>
              </>
            )}
          </div>
          {!isVerkoper && (
            <button
              type="button"
              onClick={() => setSettingsOpen(true)}
              className="px-3 py-2 text-sm rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5 shadow-sm"
              data-testid="kb-settings-btn"
              title="Instellingen"
            >
              <Gear size={16} />
            </button>
          )}
          <button
            type="button"
            onClick={() => load()}
            disabled={busy}
            className="px-3 py-2 text-sm rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5 shadow-sm"
            data-testid="kb-refresh-btn"
            title="Vernieuwen"
          >
            <ArrowsClockwise size={16} className={busy ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* 4 stat-cards: compacter design */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 print:grid-cols-4">
        <div className="rounded-xl p-2 sm:p-3 bg-white border border-slate-200 shadow-sm">
          <div className="text-xl sm:text-2xl font-bold text-slate-900">{data.totalen.totaal}</div>
          <div className="text-[10px] sm:text-[11px] text-slate-500 uppercase tracking-wide mt-0.5">Klanten totaal</div>
        </div>
        <button
          onClick={() => setFilterPrio(filterPrio === 'rood' ? '' : 'rood')}
          className={`rounded-xl p-2 sm:p-3 text-left bg-white border shadow-sm transition-all ${filterPrio === 'rood' ? 'border-red-400 ring-2 ring-red-200' : 'border-slate-200 hover:border-red-300'}`}
          data-testid="kb-stat-geen-aankoop"
        >
          <div className="text-xl sm:text-2xl font-bold text-red-700">{data.totalen.geen_aankoop || 0}</div>
          <div className="text-[10px] sm:text-[11px] text-red-600 uppercase tracking-wide mt-0.5 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
            <span className="truncate">Geen aankoop &gt; {data.settings.drempel_aankoop_dagen}d</span>
          </div>
        </button>
        <button
          onClick={() => setFilterPrio(filterPrio === 'oranje' ? '' : 'oranje')}
          className={`rounded-xl p-2 sm:p-3 text-left bg-white border shadow-sm transition-all ${filterPrio === 'oranje' ? 'border-amber-400 ring-2 ring-amber-200' : 'border-slate-200 hover:border-amber-300'}`}
          data-testid="kb-stat-niet-bezocht"
        >
          <div className="text-xl sm:text-2xl font-bold text-amber-700">{data.totalen.niet_deze_maand || 0}</div>
          <div className="text-[10px] sm:text-[11px] text-amber-600 uppercase tracking-wide mt-0.5 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
            <span className="truncate">Niet bezocht deze maand</span>
          </div>
        </button>
        <button
          onClick={() => setFilterPrio(filterPrio === 'groen' ? '' : 'groen')}
          className={`rounded-xl p-2 sm:p-3 text-left bg-white border shadow-sm transition-all ${filterPrio === 'groen' ? 'border-emerald-400 ring-2 ring-emerald-200' : 'border-slate-200 hover:border-emerald-300'}`}
          data-testid="kb-stat-wel-bezocht"
        >
          <div className="text-xl sm:text-2xl font-bold text-emerald-700">{data.totalen.wel_deze_maand || 0}</div>
          <div className="text-[10px] sm:text-[11px] text-emerald-600 uppercase tracking-wide mt-0.5 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
            <span className="truncate">Wel bezocht deze maand</span>
          </div>
        </button>
      </div>

      {/* Hoofd-card: Te bezoeken klanten — maand */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Card header met "Alles selecteren" + "Verstuur via WhatsApp" */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 sm:px-6 py-3 border-b border-slate-200 bg-white">
          <div>
            <h2 className="text-base sm:text-lg font-bold text-slate-900">Te bezoeken klanten — {fmtMaand(data.vandaag) || 'deze maand'}</h2>
          </div>
          <div className="flex items-center justify-between sm:justify-end gap-3 print:hidden">
            <button
              type="button"
              onClick={toggleSelectAll}
              className="text-xs sm:text-sm text-slate-600 hover:text-slate-900 hover:underline whitespace-nowrap"
              data-testid="kb-select-all-link"
            >
              {allSelected ? 'Wis selectie' : 'Alles selecteren'}
            </button>
            <button
              type="button"
              onClick={openWhatsapp}
              disabled={selectedIds.size === 0}
              className="px-3 sm:px-4 py-2 text-xs sm:text-sm rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold inline-flex items-center gap-1.5 shadow-sm transition-all whitespace-nowrap"
              data-testid="kb-bulk-whatsapp"
            >
              <WhatsappLogo size={14} weight="fill" />
              <span className="hidden sm:inline">Verstuur via</span> WhatsApp
            </button>
          </div>
        </div>

        {/* Filter-strip: zoek + stad + prio reset */}
        <div className="flex flex-wrap items-center gap-2 px-4 sm:px-6 py-2 border-b border-slate-100 bg-slate-50 print:hidden">
          <Funnel size={14} className="text-slate-400" />
          <input
            type="text"
            value={filterStad}
            onChange={e => setFilterStad(e.target.value)}
            placeholder="Stad..."
            className="text-sm border border-slate-200 rounded px-2 py-1 bg-white w-32"
            data-testid="kb-filter-stad"
          />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="🔍 Zoek naam, klantnr, tel, mail..."
            className="text-sm border border-slate-200 rounded px-2 py-1 bg-white flex-1 min-w-[200px]"
            data-testid="kb-search"
          />
          {filterPrio && (
            <button
              type="button"
              onClick={() => setFilterPrio('')}
              className="text-xs px-2 py-1 rounded bg-slate-200 hover:bg-slate-300 inline-flex items-center gap-1"
            >
              <X size={12} /> Wis prio-filter
            </button>
          )}
          <div className="text-xs text-slate-500 ml-auto">{filtered.length} van {data.totalen.totaal} klanten</div>
        </div>

        {/* Tabel (desktop + tablet, vanaf md) */}
        <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500 tracking-wide">
              <tr>
                <th className="px-3 py-3 text-center w-10 print:hidden"></th>
                <th className="px-3 py-3 text-left">Klant</th>
                <th className="px-3 py-3 text-left">Status</th>
                <th className="px-3 py-3 text-left">Verkoper</th>
                <th className="px-3 py-3 text-left hidden lg:table-cell">Laatste bezoek</th>
                <th className="px-3 py-3 text-left hidden lg:table-cell">Laatste aankoop</th>
                <th className="px-3 py-3 text-right pr-4 print:hidden">Acties</th>
              </tr>
            </thead>
            <tbody>
              {busy && (
                <tr><td colSpan={7} className="p-6 text-center text-slate-500">Laden...</td></tr>
              )}
              {!busy && filtered.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-slate-500" data-testid="kb-empty">
                  {data.totalen.totaal === 0 ? 'Nog geen klanten. Importeer of voeg klanten toe.' : 'Geen klanten matchen de filters.'}
                </td></tr>
              )}
              {!busy && filtered.map(k => {
                const style = PRIO_STYLE[k.prioriteit?.kleur] || PRIO_STYLE.grijs;
                const checked = selectedIds.has(k.id);
                const isPriority = k.prioriteit?.kleur === 'rood' || k.prioriteit?.kleur === 'paars';
                return (
                  <tr
                    key={k.id}
                    data-kb-selected={checked ? '1' : '0'}
                    className={`border-b border-slate-100 transition-colors ${
                      checked ? 'bg-blue-50/60' : isPriority ? 'bg-red-50/30 hover:bg-red-50/50' : 'hover:bg-slate-50'
                    }`}
                    data-testid={`kb-row-${k.id}`}
                  >
                    <td className="px-3 py-3 text-center print:hidden">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSel(k.id)}
                        className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500"
                        data-testid={`kb-check-${k.id}`}
                      />
                    </td>
                    <td className="px-3 py-3">
                      <button
                        type="button"
                        onClick={() => navigate(`/klanten?focus=${k.id}`)}
                        className="font-semibold text-slate-900 hover:text-[#002FA7] hover:underline text-left block"
                        data-testid={`kb-naam-${k.id}`}
                      >
                        {k.naam}
                      </button>
                      <div className="text-xs text-slate-500 mt-0.5">{k.adres_short || k.klantnr}</div>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`} data-testid={`kb-status-${k.id}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
                        {k.prioriteit?.label || '—'}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-slate-700">
                      {!isVerkoper ? (
                        <select
                          value={k.verkoper_id || ''}
                          onChange={(e) => handleVerkoperChange(k.id, e.target.value)}
                          className={`text-xs border rounded px-2 py-1 bg-white min-w-[120px] ${k.verkoper_id ? 'border-slate-200 text-slate-700' : 'border-amber-300 text-amber-700 bg-amber-50'}`}
                          data-testid={`kb-verkoper-select-${k.id}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="">⚠️ niet toegewezen</option>
                          {(data.verkopers || []).map(v => (
                            <option key={v.id} value={v.id}>{v.naam}</option>
                          ))}
                        </select>
                      ) : (
                        k.verkoper_naam || <span className="text-amber-600">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3 hidden lg:table-cell text-slate-600 text-sm">
                      {k.laatste_bezoek ? fmtDate(k.laatste_bezoek) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-3 hidden lg:table-cell text-slate-600 text-sm">
                      {k.laatste_aankoop ? fmtDate(k.laatste_aankoop) : <span className="text-slate-400">geen</span>}
                    </td>
                    <td className="px-3 py-3 pr-4 print:hidden">
                      <div className="flex gap-1.5 justify-end items-center">
                        <button
                          type="button"
                          onClick={() => handleKlantWhatsapp(k)}
                          disabled={!(k.mobiel || k.tel)}
                          className="p-2 text-sm rounded-lg hover:bg-emerald-50 text-emerald-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          title={(k.mobiel || k.tel) ? "Stuur klant via WhatsApp Hub" : "Geen telefoonnummer"}
                          data-testid={`kb-wa-klant-${k.id}`}
                        >
                          <WhatsappLogo size={18} weight="fill" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleHistoriek(k)}
                          className="p-2 text-sm rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"
                          title="Toon historiek"
                          data-testid={`kb-historiek-${k.id}`}
                        >
                          <ChartBar size={16} />
                        </button>
                        {k.laatste_bezoek && (
                          <button
                            type="button"
                            onClick={() => handleUndo(k.id)}
                            className="p-2 text-sm rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"
                            title="Laatste bezoek wissen"
                            data-testid={`kb-undo-${k.id}`}
                          >
                            ↶
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleRegistreer(k.id)}
                          className="ml-1 px-3 py-2 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold inline-flex items-center gap-1.5 shadow-sm transition-all"
                          title="Markeer bezocht vandaag"
                          data-testid={`kb-bezocht-${k.id}`}
                        >
                          <Check size={14} weight="bold" />
                          Bezoek vandaag
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile card-list (alleen op kleine schermen) */}
        <div className="md:hidden divide-y divide-slate-100">
          {busy && (
            <div className="p-6 text-center text-slate-500">Laden...</div>
          )}
          {!busy && filtered.length === 0 && (
            <div className="p-8 text-center text-slate-500" data-testid="kb-empty-mobile">
              {data.totalen.totaal === 0 ? 'Nog geen klanten.' : 'Geen klanten matchen de filters.'}
            </div>
          )}
          {!busy && filtered.map(k => {
            const style = PRIO_STYLE[k.prioriteit?.kleur] || PRIO_STYLE.grijs;
            const checked = selectedIds.has(k.id);
            const isPriority = k.prioriteit?.kleur === 'rood' || k.prioriteit?.kleur === 'paars';
            return (
              <div
                key={k.id}
                data-kb-selected={checked ? '1' : '0'}
                className={`p-3 ${checked ? 'bg-blue-50/60' : isPriority ? 'bg-red-50/30' : 'bg-white'}`}
                data-testid={`kb-card-mobile-${k.id}`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleSel(k.id)}
                    className="mt-1 w-5 h-5 rounded text-emerald-600 focus:ring-emerald-500 flex-shrink-0"
                    data-testid={`kb-check-${k.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    {/* Naam + status badge */}
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <button
                        type="button"
                        onClick={() => navigate(`/klanten?focus=${k.id}`)}
                        className="font-semibold text-slate-900 hover:underline text-left flex-1 min-w-0 truncate"
                      >
                        {k.naam}
                      </button>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${style.bg} ${style.text} flex-shrink-0`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
                        {k.prioriteit?.kleur === 'rood' ? `${k.aankoop_dagen || '?'}d` : (k.prioriteit?.kleur === 'groen' ? 'OK' : (k.bezoek_dagen != null ? `${k.bezoek_dagen}d` : 'nooit'))}
                      </span>
                    </div>
                    {/* Adres */}
                    <div className="text-xs text-slate-500 mb-1.5 truncate">{k.adres_short || k.klantnr}</div>
                    {/* Verkoper-dropdown (admin) of label (verkoper) */}
                    {!isVerkoper ? (
                      <select
                        value={k.verkoper_id || ''}
                        onChange={(e) => handleVerkoperChange(k.id, e.target.value)}
                        className={`text-xs border rounded px-2 py-1 bg-white w-full mb-2 ${k.verkoper_id ? 'border-slate-200 text-slate-700' : 'border-amber-300 text-amber-700 bg-amber-50'}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="">⚠️ niet toegewezen</option>
                        {(data.verkopers || []).map(v => (
                          <option key={v.id} value={v.id}>{v.naam}</option>
                        ))}
                      </select>
                    ) : (
                      <div className="text-xs text-slate-600 mb-2">
                        {k.verkoper_naam || <span className="text-amber-600">— niet toegewezen —</span>}
                      </div>
                    )}
                    {/* Datums */}
                    <div className="flex gap-3 text-[11px] text-slate-500 mb-2">
                      <span>📅 Bezoek: {k.laatste_bezoek ? fmtDate(k.laatste_bezoek) : '—'}</span>
                      <span>💰 Aankoop: {k.laatste_aankoop ? fmtDate(k.laatste_aankoop) : 'geen'}</span>
                    </div>
                    {/* Actie-knoppen rij */}
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => handleRegistreer(k.id)}
                        className="flex-1 px-3 py-2 text-xs rounded-lg bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white font-semibold inline-flex items-center justify-center gap-1.5 shadow-sm"
                        data-testid={`kb-bezocht-${k.id}`}
                      >
                        <Check size={14} weight="bold" />
                        Bezocht
                      </button>
                      <button
                        type="button"
                        onClick={() => handleKlantWhatsapp(k)}
                        disabled={!(k.mobiel || k.tel)}
                        className="p-2 rounded-lg bg-emerald-50 text-emerald-600 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid={`kb-wa-klant-${k.id}`}
                      >
                        <WhatsappLogo size={18} weight="fill" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleHistoriek(k)}
                        className="p-2 rounded-lg bg-slate-50 text-slate-500 active:scale-95"
                        data-testid={`kb-historiek-${k.id}`}
                      >
                        <ChartBar size={16} />
                      </button>
                      {k.laatste_bezoek && (
                        <button
                          type="button"
                          onClick={() => handleUndo(k.id)}
                          className="p-2 rounded-lg bg-slate-50 text-slate-500 active:scale-95"
                          data-testid={`kb-undo-${k.id}`}
                        >
                          ↶
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* "Hoe de volgorde werkt:" uitleg-banner (zoals mockup) */}
      <div className="bg-slate-100 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700 print:hidden">
        <strong className="font-semibold">Hoe de volgorde werkt:</strong> klanten zonder aankoop binnen je ingestelde grens ({data.settings.drempel_aankoop_dagen} dagen) krijgen rood prioriteit. Daarna komen klanten die deze maand nog niet bezocht zijn. De rest staat onderaan.
      </div>

      {/* Print footer */}
      <div className="hidden print:block text-xs text-gray-500 mt-4 text-center">
        BillFac · Geprint op {new Date().toLocaleString('nl-BE')}
      </div>

      {/* Bulk-bar (verschijnt enkel met selectie, klein onderaan) */}
      {selectedIds.size > 0 && (
        <div className="bf-bottombar fixed bottom-4 right-4 bg-white border-2 border-[#002FA7] rounded-xl shadow-2xl p-3 flex items-center gap-3 z-40 print:hidden" data-testid="kb-bulk-bar">
          <span className="text-sm font-semibold text-[#002FA7]">{selectedIds.size} geselecteerd</span>
          <button
            type="button"
            onClick={handleBulkRegistreer}
            className="px-3 py-1.5 text-sm rounded bg-emerald-600 hover:bg-emerald-700 text-white font-semibold inline-flex items-center gap-1.5"
            data-testid="kb-bulk-bezocht"
          >
            <Check size={14} weight="bold" /> Bezocht
          </button>
          <button
            type="button"
            onClick={() => navigate('/klantbezoeken/route', { state: { klant_ids: Array.from(selectedIds) } })}
            className="px-3 py-1.5 text-sm rounded bg-[#002FA7] hover:bg-[#001A66] text-white font-semibold inline-flex items-center gap-1.5"
            data-testid="kb-bulk-route"
            title="Bezoekroute berekenen voor de geselecteerde klanten"
          >
            <Compass size={14} weight="bold" /> Route
          </button>
          <button
            type="button"
            onClick={handleBulkWhatsappHub}
            className="px-3 py-1.5 text-sm rounded bg-slate-200 hover:bg-slate-300 text-slate-700 font-medium inline-flex items-center gap-1.5"
            data-testid="kb-bulk-whatsapp-hub"
            title="Open WhatsApp Hub met queue van geselecteerde klanten"
          >
            <PaperPlaneTilt size={14} /> Hub
          </button>
          <button
            type="button"
            onClick={() => setSelectedIds(new Set())}
            className="text-xs text-slate-400 hover:text-slate-600"
          >
            ✕
          </button>
        </div>
      )}

      {/* Settings modal */}
      {settingsOpen && (
        <SettingsModal
          settings={data.settings}
          onClose={() => setSettingsOpen(false)}
          onSaved={() => { setSettingsOpen(false); load(); }}
        />
      )}

      {/* WhatsApp modal */}
      {whatsappOpen && (
        <WhatsappModal
          verkopers={data.verkopers}
          verkoperId={whatsappVerkoperId}
          setVerkoperId={setWhatsappVerkoperId}
          waData={whatsappData}
          klantAantal={selectedIds.size}
          selectedByVerkoper={selectedByVerkoper}
          whatsappGroups={whatsappGroups}
          onVerstuurPerVerkoper={handleVerstuurPerVerkoper}
          onClose={() => { setWhatsappOpen(false); setWhatsappData(null); setWhatsappGroups([]); }}
        />
      )}

      {/* Historiek modal */}
      {historiekKlant && (
        <HistoriekModal
          klant={historiekKlant}
          historiek={historiek}
          onClose={() => { setHistoriekKlant(null); setHistoriek([]); }}
        />
      )}

      {/* Print-specifieke CSS */}
      <style>{`
        @media print {
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          .print\\:block { display: block !important; }
          .print\\:grid-cols-5 { grid-template-columns: repeat(5, minmax(0, 1fr)) !important; }
          table { font-size: 10px !important; }
          thead { background: #f3f4f6 !important; }
          /* Als de gebruiker een selectie heeft: verberg niet-geselecteerde rijen tijdens print */
          body.kb-print-selected-only tr[data-kb-selected="0"] { display: none !important; }
        }
      `}</style>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Sub-componenten
// ═══════════════════════════════════════════════════════════════════════════

function SettingsModal({ settings, onClose, onSaved }) {
  const [oranje, setOranje] = useState(settings.drempel_oranje);
  const [rood, setRood] = useState(settings.drempel_rood);
  const [paars, setPaars] = useState(settings.drempel_paars);
  const [aankoop, setAankoop] = useState(settings.drempel_aankoop_dagen || 60);
  const [landcode, setLandcode] = useState(settings.landcode_whatsapp || '32');
  const [saving, setSaving] = useState(false);

  const valid = oranje > 0 && rood > oranje && paars > rood && aankoop > 0;

  const save = async () => {
    setSaving(true);
    try {
      await API.put('/klantbezoeken/instellingen', {
        drempel_oranje: oranje,
        drempel_rood: rood,
        drempel_paars: paars,
        drempel_aankoop_dagen: aankoop,
        landcode_whatsapp: landcode,
      });
      toast.success('Instellingen opgeslagen');
      onSaved();
    } catch (e) {
      toast.error('Fout: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-lg p-4 sm:p-6 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="kb-settings-modal">
        <h2 className="text-lg font-bold mb-1 flex items-center gap-2"><Gear size={20} /> Klantbezoeken — instellingen</h2>
        <p className="text-xs text-gray-500 mb-4">Drempels in dagen sinds laatste bezoek of aankoop.</p>

        {/* Sales-prioriteit */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <label className="text-sm font-bold text-red-800 flex items-center gap-2">🔴 Sales-prioriteit (geen aankoop)</label>
          <p className="text-xs text-red-700 mt-0.5 mb-2">Klanten die al X dagen niets meer kochten staan ALTIJD bovenaan.</p>
          <input type="number" value={aankoop} onChange={e => setAankoop(parseInt(e.target.value) || 0)} className="w-full border border-red-300 rounded px-2 py-1.5" min="1" data-testid="kb-set-aankoop" />
          <div className="text-xs text-red-700 mt-0.5">Rood na {aankoop || 0} dagen zonder aankoop</div>
        </div>

        {/* Bezoek-drempels */}
        <div className="space-y-3">
          <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Bezoek-drempels</div>
          <div>
            <label className="text-sm font-medium text-amber-800 flex items-center gap-2">🟠 Oranje (binnenkort bezoeken)</label>
            <input type="number" value={oranje} onChange={e => setOranje(parseInt(e.target.value) || 0)} className="w-full mt-1 border border-gray-300 rounded px-2 py-1.5" min="1" data-testid="kb-set-oranje" />
            <div className="text-xs text-gray-500 mt-0.5">Vanaf {oranje || 0} dagen niet bezocht</div>
          </div>
          <div>
            <label className="text-sm font-medium text-red-800 flex items-center gap-2">🔴 Rood (dringend)</label>
            <input type="number" value={rood} onChange={e => setRood(parseInt(e.target.value) || 0)} className="w-full mt-1 border border-gray-300 rounded px-2 py-1.5" min="1" data-testid="kb-set-rood" />
            <div className="text-xs text-gray-500 mt-0.5">Vanaf {rood || 0} dagen niet bezocht</div>
          </div>
          <div>
            <label className="text-sm font-medium text-purple-800 flex items-center gap-2">🟣 Paars (kritiek lang niet bezocht)</label>
            <input type="number" value={paars} onChange={e => setPaars(parseInt(e.target.value) || 0)} className="w-full mt-1 border border-gray-300 rounded px-2 py-1.5" min="1" data-testid="kb-set-paars" />
            <div className="text-xs text-gray-500 mt-0.5">Vanaf {paars || 0} dagen niet bezocht</div>
          </div>
        </div>

        {/* WhatsApp landcode */}
        <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <label className="text-sm font-bold text-emerald-800 flex items-center gap-2"><WhatsappLogo size={16} weight="fill" className="text-[#25D366]" /> WhatsApp-landcode (zonder +)</label>
          <p className="text-xs text-emerald-700 mt-0.5 mb-2">Voor telefoonnummers die beginnen met 0 (lokaal formaat).</p>
          <input type="text" value={landcode} onChange={e => setLandcode(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))} className="w-full border border-emerald-300 rounded px-2 py-1.5" data-testid="kb-set-landcode" placeholder="32" />
          <div className="text-xs text-emerald-700 mt-0.5">32 = België · 31 = Nederland · 33 = Frankrijk · 49 = Duitsland · 44 = UK</div>
        </div>

        {!valid && (
          <div className="mt-3 text-xs text-red-600 bg-red-50 p-2 rounded">
            ⚠️ Drempels moeten oplopen: oranje &lt; rood &lt; paars, en aankoop &gt; 0
          </div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded border border-gray-300 hover:bg-gray-50">Annuleren</button>
          <button type="button" onClick={save} disabled={!valid || saving} className="px-4 py-2 text-sm rounded bg-[#002FA7] hover:bg-[#001A66] text-white font-semibold disabled:opacity-50" data-testid="kb-set-save">
            {saving ? 'Opslaan...' : 'Opslaan'}
          </button>
        </div>
      </div>
    </div>
  );
}

function WhatsappModal({ verkopers, verkoperId, setVerkoperId, waData, klantAantal, selectedByVerkoper, whatsappGroups, onVerstuurPerVerkoper, onClose }) {
  const [mode, setMode] = useState('one');  // 'one' = 1 bericht naar 1 verkoper · 'split' = per toegewezen verkoper
  const verkoper = verkopers.find(v => v.id === verkoperId);
  const heeftTel = waData?.telefoon && waData.telefoon !== '';

  // Stats voor split-mode
  const verkopersInSelectie = selectedByVerkoper || [];
  const groepenMetVerkoper = verkopersInSelectie.filter(g => g.verkoper_id);
  const aantalZonderVerkoper = verkopersInSelectie.find(g => !g.verkoper_id)?.klant_ids.length || 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl w-full sm:max-w-2xl p-4 sm:p-6 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="kb-whatsapp-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2">
              <WhatsappLogo size={22} className="text-[#25D366]" weight="fill" />
              Verstuur via WhatsApp
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              <strong>{klantAantal}</strong> {klantAantal === 1 ? 'klant' : 'klanten'} geselecteerd
              {groepenMetVerkoper.length > 1 && ` · verdeeld over ${groepenMetVerkoper.length} verkopers`}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>

        {/* Mode-tabs */}
        <div className="flex gap-1 mb-4 bg-slate-100 rounded-lg p-1">
          <button
            type="button"
            onClick={() => setMode('one')}
            className={`flex-1 px-3 py-2 text-sm rounded-md font-medium transition-colors ${mode === 'one' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:text-slate-900'}`}
            data-testid="kb-wa-mode-one"
          >
            📤 Alle naar 1 verkoper
          </button>
          <button
            type="button"
            onClick={() => setMode('split')}
            disabled={groepenMetVerkoper.length === 0}
            className={`flex-1 px-3 py-2 text-sm rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${mode === 'split' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:text-slate-900'}`}
            data-testid="kb-wa-mode-split"
          >
            🔀 Per toegewezen verkoper {groepenMetVerkoper.length > 0 && `(${groepenMetVerkoper.length})`}
          </button>
        </div>

        {mode === 'one' && (
          <>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Ontvanger (verkoper)</label>
              <select
                value={verkoperId}
                onChange={e => setVerkoperId(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
                data-testid="kb-wa-verkoper"
              >
                <option value="">— Geen specifieke (handmatig kiezen na openen) —</option>
                {verkopers.map(v => (
                  <option key={v.id} value={v.id}>
                    {v.naam} {v.tel ? `· ${v.tel}` : '(geen telefoon)'}
                  </option>
                ))}
              </select>
              {verkoper && !verkoper.tel && (
                <div className="text-xs text-amber-700 mt-1 bg-amber-50 p-1.5 rounded">
                  ⚠️ Deze verkoper heeft geen telefoonnummer. WhatsApp link werkt enkel als handmatige share.
                </div>
              )}
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mt-3">
              <div className="text-xs text-slate-500 mb-1">Voorbeeld bericht ({klantAantal} {klantAantal === 1 ? 'klant' : 'klanten'})</div>
              <pre className="text-xs whitespace-pre-wrap font-mono text-slate-700 max-h-64 overflow-y-auto" data-testid="kb-wa-preview">
                {waData?.tekst || 'Bezig met genereren...'}
              </pre>
            </div>

            {waData?.wa_url && (
              <div className="flex flex-wrap gap-2 mt-3">
                <a
                  href={waData.wa_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 px-4 py-2.5 rounded-lg bg-[#25D366] hover:bg-[#22BF5C] text-white font-semibold text-center inline-flex items-center justify-center gap-2"
                  data-testid="kb-wa-open"
                >
                  <WhatsappLogo size={18} weight="fill" />
                  {heeftTel ? `Open WhatsApp → ${waData.telefoon}` : 'Open WhatsApp (kies contact)'}
                </a>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(waData.tekst);
                    toast.success('Tekst gekopieerd');
                  }}
                  className="px-4 py-2.5 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm font-medium"
                  data-testid="kb-wa-copy"
                >
                  📋 Kopieer tekst
                </button>
              </div>
            )}
          </>
        )}

        {mode === 'split' && (
          <>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 mb-3">
              <strong>🔀 Per toegewezen verkoper:</strong> elke verkoper ontvangt enkel de klanten die aan hém zijn toegewezen.
              {aantalZonderVerkoper > 0 && (
                <div className="mt-1 text-amber-700">⚠️ {aantalZonderVerkoper} klant(en) zonder toegewezen verkoper worden overgeslagen — wijs eerst een verkoper toe in de tabel.</div>
              )}
            </div>

            <div className="space-y-2">
              {groepenMetVerkoper.map(g => {
                const result = whatsappGroups.find(r => r.verkoper_id === g.verkoper_id);
                const verkoperFull = verkopers.find(v => v.id === g.verkoper_id);
                return (
                  <div key={g.verkoper_id} className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                    <div className="flex items-center justify-between mb-1">
                      <div>
                        <div className="font-semibold text-slate-900">{g.verkoper_naam}</div>
                        <div className="text-xs text-slate-500">
                          {g.klant_ids.length} {g.klant_ids.length === 1 ? 'klant' : 'klanten'}
                          {verkoperFull?.tel ? ` · ${verkoperFull.tel}` : ' · ⚠️ geen telefoon'}
                        </div>
                      </div>
                      {result?.wa_url ? (
                        <a
                          href={result.wa_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1.5 rounded bg-[#25D366] hover:bg-[#22BF5C] text-white text-xs font-semibold inline-flex items-center gap-1.5"
                          data-testid={`kb-wa-open-${g.verkoper_id}`}
                        >
                          <WhatsappLogo size={14} weight="fill" />
                          Open WhatsApp
                        </a>
                      ) : (
                        <span className="text-xs text-slate-400">Wacht op genereren...</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {whatsappGroups.length === 0 ? (
              <button
                type="button"
                onClick={onVerstuurPerVerkoper}
                disabled={groepenMetVerkoper.length === 0}
                className="w-full mt-4 px-4 py-2.5 rounded-lg bg-[#25D366] hover:bg-[#22BF5C] disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold inline-flex items-center justify-center gap-2"
                data-testid="kb-wa-genereer-per-verkoper"
              >
                <PaperPlaneTilt size={18} weight="fill" />
                Genereer {groepenMetVerkoper.length} WhatsApp-link{groepenMetVerkoper.length === 1 ? '' : 's'}
              </button>
            ) : (
              <div className="text-xs text-emerald-700 bg-emerald-50 p-2 rounded mt-3 text-center">
                ✅ {whatsappGroups.length} link{whatsappGroups.length === 1 ? '' : 's'} klaar — klik op &quot;Open WhatsApp&quot; per verkoper hierboven.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function HistoriekModal({ klant, historiek, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-lg p-4 sm:p-6 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="kb-historiek-modal">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="text-lg font-bold">Bezoek-historiek</h2>
            <div className="text-sm text-gray-600">{klant.naam} · {klant.klantnr}</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        {historiek.length === 0 ? (
          <div className="text-center text-gray-500 py-8">Nog geen bezoeken geregistreerd</div>
        ) : (
          <ul className="space-y-2">
            {historiek.map(h => (
              <li key={h.id} className="border border-gray-200 rounded p-2 flex items-start gap-2">
                <Calendar size={16} className="text-[#002FA7] mt-0.5" />
                <div className="flex-1 text-sm">
                  <div className="font-medium">{fmtDate(h.datum)}</div>
                  {h.notitie && <div className="text-gray-600 text-xs mt-0.5">{h.notitie}</div>}
                  <div className="text-[11px] text-gray-400 mt-0.5">
                    door {h.geregistreerd_door || '—'}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
